// J.2 — Admin Frame / Blanket Orders page.
//
// A frame caps freight count AND/OR total amount over a validity window.
// Either cap is optional (null = unlimited). Whichever cap exhausts
// first triggers the `on_exceed` mode (warn / block / force_approval).
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axios from "axios";
import { ChevronDown, ChevronUp, Plus, Trash2, Save, Loader2, AlertCircle, X, Boxes, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/frame-orders`;

const mono = (s, c, w, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: s,
  color: c || "var(--text, #f0f2f8)",
  fontWeight: w || 400,
  letterSpacing: ls,
});
const syne = (s, w, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: s,
  fontWeight: w || 700,
  letterSpacing: ls,
});

const _inp = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#f0f2f8",
  ...mono(11),
  padding: "5px 8px",
  borderRadius: 4,
  width: "100%",
};

const useFrames = (customerFilter, status) =>
  useQuery({
    queryKey: ["frame-orders", customerFilter || "_all", status],
    queryFn: () => axios.get(
      `${API}?status=${status}${customerFilter ? `&customer_tenant_id=${customerFilter}` : ""}`
    ).then((r) => r.data),
    staleTime: 30_000,
  });

const useCreateFrame = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(API, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["frame-orders"] }),
  });
};
const useDeleteFrame = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fid) => axios.delete(`${API}/${fid}`).then(() => fid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["frame-orders"] }),
  });
};
const useCalloffs = (fid, enabled) =>
  useQuery({
    queryKey: ["frame-calloffs", fid],
    queryFn: () => axios.get(`${API}/${fid}/calloffs`).then((r) => r.data),
    enabled: !!enabled,
  });

const fmtMoney = (v, ccy = "EUR") => {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  const txt = n.toLocaleString(undefined, { minimumFractionDigits: ccy === "HUF" ? 0 : 2, maximumFractionDigits: ccy === "HUF" ? 0 : 2 });
  return ccy === "HUF" ? `${txt} Ft` : `€ ${txt}`;
};

const UsageBar = ({ pct, label, testid }) => {
  const isOver = pct != null && pct > 100;
  const isNear = pct != null && pct >= 90 && pct <= 100;
  const c = isOver ? "#ef4444" : isNear ? "#f3b14a" : "#0a8754";
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <div className="flex items-center justify-between" style={mono(9, "#94a3b8", 700, "0.4px")}>
        <span style={{ textTransform: "uppercase" }}>{label}</span>
        <span style={{ color: c, fontWeight: 700 }}>{pct == null ? "—" : `${pct.toFixed(1)}%`}</span>
      </div>
      <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{
          width: pct == null ? 0 : `${Math.min(pct, 100)}%`,
          height: "100%", background: c, transition: "width 200ms ease",
        }} />
      </div>
    </div>
  );
};

export default function AdminFrameOrders() {
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("active");
  const { data, isLoading, error } = useFrames(filter, status);
  const createF = useCreateFrame();
  const deleteF = useDeleteFrame();

  const [newOpen, setNewOpen] = useState(false);
  const [draft, setDraft] = useState({
    customer_tenant_id: "", name: "",
    cap_freight_count: "", cap_total_amount: "",
    currency: "EUR", on_exceed: "force_approval",
    valid_from: "", valid_to: "", note: "",
  });

  const [expanded, setExpanded] = useState(null); // frame id

  const submit = async () => {
    const body = {
      customer_tenant_id: draft.customer_tenant_id.trim(),
      name: draft.name.trim(),
      cap_freight_count: draft.cap_freight_count === "" ? null : Number(draft.cap_freight_count),
      cap_total_amount: draft.cap_total_amount === "" ? null : Number(draft.cap_total_amount),
      currency: draft.currency,
      on_exceed: draft.on_exceed,
      valid_from: draft.valid_from || null,
      valid_to: draft.valid_to || null,
      note: draft.note || null,
    };
    if (!body.customer_tenant_id || !body.name) {
      toast.error("Customer + name required");
      return;
    }
    if (body.cap_freight_count == null && body.cap_total_amount == null) {
      toast.error("At least one cap is required");
      return;
    }
    try {
      await createF.mutateAsync(body);
      toast.success("Frame created");
      setNewOpen(false);
      setDraft({ ...draft, customer_tenant_id: "", name: "", cap_freight_count: "", cap_total_amount: "", note: "" });
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || det?.code || "Create failed");
    }
  };

  if (error?.response?.status === 403) {
    return (
      <div data-testid="frame-orders-403" className="rounded-lg p-6" style={{ background: "rgba(220,53,69,0.06)", border: "1px solid rgba(220,53,69,0.30)" }}>
        <AlertCircle size={16} color="#ef4444" className="inline mr-2" />
        <span style={mono(12, "#ef4444", 700)}>Frame orders are forwarder-internal only.</span>
      </div>
    );
  }

  const items = data?.items || [];

  return (
    <div data-testid="admin-frame-orders-page">
      <header className="mb-3">
        <h1 style={syne("clamp(22px, 3vw, 28px)", 800, "-0.5px")} data-testid="view-title">
          <Boxes size={20} className="inline mr-2" />
          Frame / Blanket Orders
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          J.2 · Caps on <span style={{ color: "#9ec3ec" }}>freight count</span> + / or <span style={{ color: "#9ec3ec" }}>total amount</span>. Both optional (null = unlimited). Whichever cap exhausts first triggers <code>on_exceed</code>.
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3" data-testid="frame-orders-toolbar">
        <input type="text" placeholder="Filter by customer_tenant_id" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ..._inp, width: 320 }} data-testid="frame-orders-filter" />
        {["active", "closed", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className="px-2 py-1 rounded" style={{
            background: status === s ? "rgba(24,95,165,0.15)" : "transparent",
            border: `1px solid ${status === s ? "rgba(24,95,165,0.50)" : "rgba(255,255,255,0.15)"}`,
            color: status === s ? "#9ec3ec" : "#94a3b8",
            ...mono(10, undefined, 700, "0.5px"), cursor: "pointer", textTransform: "uppercase",
          }} data-testid={`frame-orders-status-${s}`}>{s}</button>
        ))}
        <button onClick={() => setNewOpen(true)} className="ml-auto px-3 py-1.5 rounded inline-flex items-center gap-1"
          style={{ background: "#0a8754", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
          data-testid="frame-orders-new-btn">
          <Plus size={11} /> New frame
        </button>
      </div>

      {newOpen && (
        <div className="rounded-lg p-3 mb-3" style={{ background: "rgba(10,135,84,0.05)", border: "1px solid rgba(10,135,84,0.30)" }} data-testid="frame-orders-new-form">
          <div className="grid grid-cols-3 gap-2 mb-2">
            <input placeholder="Customer tenant id *" value={draft.customer_tenant_id} onChange={(e) => setDraft({ ...draft, customer_tenant_id: e.target.value })} style={_inp} data-testid="frame-orders-new-customer" />
            <input placeholder="Frame name *" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={_inp} data-testid="frame-orders-new-name" />
            <select value={draft.on_exceed} onChange={(e) => setDraft({ ...draft, on_exceed: e.target.value })} style={_inp} data-testid="frame-orders-new-on-exceed">
              <option value="force_approval">On exceed: force approval (default)</option>
              <option value="warn">On exceed: warn only</option>
              <option value="block">On exceed: block</option>
            </select>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <input type="number" placeholder="Cap freight count (blank = unlimited)" value={draft.cap_freight_count} onChange={(e) => setDraft({ ...draft, cap_freight_count: e.target.value })} style={_inp} data-testid="frame-orders-new-cap-count" />
            <input type="number" step="0.01" placeholder="Cap total amount (blank = unlimited)" value={draft.cap_total_amount} onChange={(e) => setDraft({ ...draft, cap_total_amount: e.target.value })} style={_inp} data-testid="frame-orders-new-cap-amount" />
            <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} style={_inp} data-testid="frame-orders-new-currency">
              {["EUR", "HUF", "USD", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <input type="date" value={draft.valid_from} onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })} style={_inp} data-testid="frame-orders-new-valid-from" />
              <span style={mono(10, "#94a3b8")}>→</span>
              <input type="date" value={draft.valid_to} onChange={(e) => setDraft({ ...draft, valid_to: e.target.value })} style={_inp} data-testid="frame-orders-new-valid-to" />
            </div>
          </div>
          <div className="mb-2">
            <input placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} style={_inp} data-testid="frame-orders-new-note" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={submit} disabled={createF.isPending} className="px-3 py-1.5 rounded inline-flex items-center gap-1"
              style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
              data-testid="frame-orders-new-submit"><Save size={11} /> {createF.isPending ? "Saving…" : "Create"}</button>
            <button onClick={() => setNewOpen(false)} className="px-2.5 py-1.5 rounded inline-flex items-center gap-1"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
              data-testid="frame-orders-new-cancel"><X size={11} /> Cancel</button>
          </div>
        </div>
      )}

      {isLoading && <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading frames…</div>}

      <div className="flex flex-col gap-2" data-testid="frame-orders-list">
        {items.length === 0 && !isLoading && (
          <div className="rounded-lg p-6 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", ...mono(11, "#94a3b8") }}>
            No frame orders yet.
          </div>
        )}
        {items.map((f) => (
          <FrameRow key={f.id} f={f} expanded={expanded === f.id} onExpand={() => setExpanded(expanded === f.id ? null : f.id)} onDelete={() => deleteF.mutate(f.id)} />
        ))}
      </div>
    </div>
  );
}

const FrameRow = ({ f, expanded, onExpand, onDelete }) => {
  const isOver = !!f.is_over_cap;
  const isNear = (f.near_cap_dimensions || []).length > 0 && !isOver;
  const tint = isOver ? "rgba(239,68,68,0.10)" : isNear ? "rgba(243,177,74,0.08)" : "rgba(255,255,255,0.04)";
  const border = isOver ? "rgba(239,68,68,0.45)" : isNear ? "rgba(243,177,74,0.40)" : "rgba(255,255,255,0.10)";
  const callQ = useCalloffs(f.id, expanded);
  return (
    <div className="rounded-lg p-3" style={{ background: tint, border: `1px solid ${border}` }} data-testid={`frame-order-row-${f.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onExpand} className="p-0.5 rounded" style={{ background: "transparent", border: "none", cursor: "pointer", color: "#c5cad8" }} data-testid={`frame-order-expand-${f.id}`}>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <span style={syne(14, 700)}>{f.name}</span>
        <span style={mono(10, "#9ec3ec", 700)}>· {f.customer_tenant_id?.substring(0, 12)}{f.customer_tenant_id?.length > 12 ? "…" : ""}</span>
        {isOver && <span style={{ ...mono(9, "#ef4444", 800, "0.5px"), padding: "2px 6px", borderRadius: 3, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.40)", textTransform: "uppercase" }}>OVER · {f.over_dimensions?.join("+")}</span>}
        {isNear && !isOver && <span style={{ ...mono(9, "#f3b14a", 800, "0.5px"), padding: "2px 6px", borderRadius: 3, background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.35)", textTransform: "uppercase" }}>NEAR CAP</span>}
        <span style={mono(9, "#94a3b8", 700, "0.4px")}>{(f.valid_from || "—")} → {(f.valid_to || "open")}</span>
        <span className="ml-auto" style={mono(10, "#94a3b8")}>{f.on_exceed}</span>
        <button onClick={() => { if (window.confirm("Delete frame?")) onDelete(); }} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.40)", color: "#b22a29", cursor: "pointer" }} data-testid={`frame-order-delete-${f.id}`}>
          <Trash2 size={11} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div className="flex flex-col gap-1">
          <UsageBar pct={f.usage_count_pct} label="Freight count" testid={`frame-order-usage-count-${f.id}`} />
          <div style={mono(10, "#c5cad8")}>{f.drawn_count} of {f.cap_freight_count == null ? "∞" : f.cap_freight_count} · {f.remaining_count == null ? "unlimited" : `${f.remaining_count} remaining`}</div>
        </div>
        <div className="flex flex-col gap-1">
          <UsageBar pct={f.usage_amount_pct} label={`Total ${f.currency}`} testid={`frame-order-usage-amount-${f.id}`} />
          <div style={mono(10, "#c5cad8")}>{fmtMoney(f.drawn_amount, f.currency)} of {f.cap_total_amount == null ? "∞" : fmtMoney(f.cap_total_amount, f.currency)} · {f.remaining_amount == null ? "unlimited" : `${fmtMoney(f.remaining_amount, f.currency)} remaining`}</div>
        </div>
      </div>
      {expanded && (
        <div className="mt-2 rounded p-2" style={{ background: "rgba(0,0,0,0.20)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`frame-order-calloffs-${f.id}`}>
          <div style={{ ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase", marginBottom: 6 }}>
            <Link2 size={10} className="inline mr-1" />Call-offs · {callQ.data?.count ?? "…"}
          </div>
          {callQ.isLoading && <div style={mono(10, "#94a3b8")}>Loading…</div>}
          {callQ.data?.items?.length === 0 && <div style={mono(10, "#94a3b8")}>No call-offs yet.</div>}
          {(callQ.data?.items || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between" style={mono(10)} data-testid={`frame-order-calloff-${c.id}`}>
              <a href={`/logistics-requests/${c.id}`} style={{ color: "#9ec3ec", fontWeight: 700 }}>{c.deal_name || c.id}</a>
              <div className="flex items-center gap-3">
                <span style={{ ...mono(9, c.deal_stage === "waiting_for_approval" ? "#f3b14a" : c.deal_stage === "rejected" ? "#ef4444" : "#0a8754", 700, "0.4px"), textTransform: "uppercase" }}>{c.deal_stage}</span>
                <span style={mono(10, "#c5cad8", 700)}>{fmtMoney(c.elfogadott_dij, c.currency || "EUR")}</span>
                <span style={mono(9, "#94a3b8")}>{(c.created_at || "").substring(0, 10)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
